<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import MessageModal from "@/components/modals/message-modal";
import Loading from "@/components/Loading";

import { Calendar } from '@fullcalendar/core'
import interactionPlugin from '@fullcalendar/interaction'
import dayGridPlugin from '@fullcalendar/daygrid'

import axios from "axios";
import { authHeader } from "../../../helpers/auth-header";

import SelectGroup from "@/components/forms/select-group";

import { RRule } from 'rrule';

/**
 * Dashboard component
 */
export default {
	components: {
		Layout,
		PageHeader,
		MessageModal,
		Loading,
		SelectGroup
	},
	data() {
		return {
			title: "Dashboard",
			items: [
				{
					text: "Dashboard",
					active: true
				}
			],
			data: {
				branch: 1
			},
			branchOptions: [],
			isLoading: true,
			calendar: null,
			windowWidth: window.innerWidth,
		};
	},
	async mounted() {
		this.retrieveBranches().then((branches) => {
            this.branchOptions = branches.map((branch) => ({
                name: branch.name,
                value: branch.id,
            }));
        });

		this.initializeCalendar();
		this.isLoading = true;
	},
	watch: {
		'data.branch': function() {
			this.initializeCalendar();
		}
	},
	methods: {
		async retrieveBranches() {
            this.loading = true;
            let branches = [];

            try {
                const item = await axios.get(`${process.env.VUE_APP_APIURL}/branch`, {
                    headers: authHeader(),
                });

                const result = await item.data;
                if (result['code'] === 'success') {
                    branches = result['data'].branches;
                }
            } catch (e) {
                console.error(e);
            }

            this.loading = false;
            return branches;
        },
		async initializeCalendar() {
			if (this.calendar) {
				this.calendar.destroy(); // Destroy the previous calendar instance
			}
			const calendarEl = document.getElementById('calendar')
			this.calendar = new Calendar(calendarEl, {
				plugins: [
					interactionPlugin,
					dayGridPlugin,
				],
				initialView: this.windowWidth < 768 ? 'dayGridDay' : 'dayGridMonth',
				eventClick: this.handleSelect,
				events: await this.getEventList(this.data.branch),
				eventDidMount: this.handleEventDidMount
			})
			this.calendar.render();
			this.isLoading = false;
		},
		async getEventList(branch) {
            this.isLoading = true;

            try {
                const result = await axios.get(`${process.env.VUE_APP_APIURL}/lark-event-list?branch=${branch}`, {
                    headers: authHeader(),
                });
                const data = result.data;

                if (data.code == 'invalid_branch_calendar' || data.code == 'invalid_branch' || data.code == 'api_fail') {
                    this.$refs.messageModal.showModal(data.message);
                }

                if (!data || typeof data !== 'object' || !data.msg || data.msg !== 'success') {
                    console.error('API response does not contain the expected data structure');
                    this.isLoading = false;
                    return [];
                }

                // Expand recurring events
                const expandedEvents = this.expandRecurringEvents(data.data.items);
                
                const events = this.formatEvents(expandedEvents);
                
                this.isLoading = false;
                return events;
            } catch (e) {
                this.isLoading = false;
                console.error('An error occurred while fetching data:', e);
                return [];
            }
        },

		timestampToSingaporeDate(timestamp) {
			const date = new Date(timestamp * 1000);
			// Use UTC methods to avoid timezone shift
			// Add 8 hours offset for Singapore (UTC+8)
			const singaporeDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
			const year = singaporeDate.getUTCFullYear();
			const month = String(singaporeDate.getUTCMonth() + 1).padStart(2, '0');
			const day = String(singaporeDate.getUTCDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		},
		
		/**
		 * Convert Unix timestamp to Singapore datetime string (YYYY-MM-DD HH:MM:SS)
		 */
		timestampToSingaporeDateTime(timestamp) {
			const date = new Date(timestamp * 1000);
			// Add 8 hours offset for Singapore (UTC+8)
			const singaporeDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
			const year = singaporeDate.getUTCFullYear();
			const month = String(singaporeDate.getUTCMonth() + 1).padStart(2, '0');
			const day = String(singaporeDate.getUTCDate()).padStart(2, '0');
			const hours = String(singaporeDate.getUTCHours()).padStart(2, '0');
			const minutes = String(singaporeDate.getUTCMinutes()).padStart(2, '0');
			const seconds = String(singaporeDate.getUTCSeconds()).padStart(2, '0');
			return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
		},

		expandRecurringEvents(events) {
			const expandedEvents = [];
			
			const startDate = new Date();
			startDate.setMonth(startDate.getMonth() - 3);
			startDate.setHours(0, 0, 0, 0);
			
			const endDate = new Date();
			endDate.setMonth(endDate.getMonth() + 6);
			endDate.setHours(23, 59, 59, 999);
			
			const masterEvents = events.filter(event => event.recurrence && event.recurrence.trim() !== '');
			const singleEvents = events.filter(event => !event.recurrence || event.recurrence.trim() === '');
			
			const occupiedDates = new Set();
			singleEvents.forEach(event => {
				if (event.start_time && event.start_time.timestamp && event.status !== 'cancelled') {
					const dateStr = this.timestampToSingaporeDate(parseInt(event.start_time.timestamp));
					occupiedDates.add(dateStr);
				}
			});
			
			masterEvents.forEach(masterEvent => {
				if (masterEvent.status === 'cancelled') return;
				
				try {
					const rule = RRule.fromString(masterEvent.recurrence);
					const occurrences = rule.between(startDate, endDate);
					
					const originalStartTimestamp = parseInt(masterEvent.start_time.timestamp);
					const originalEndTimestamp = parseInt(masterEvent.end_time.timestamp);
					// const durationSeconds = originalEndTimestamp - originalStartTimestamp;
					const isAllDayEvent = this.isAllDayEvent(masterEvent);

					// Extract time-of-day using Singapore offset (+8h), not raw UTC
					const SGT_OFFSET = 8 * 60 * 60 * 1000;
					const sgStart = new Date(originalStartTimestamp * 1000 + SGT_OFFSET);
					const sgEnd = new Date(originalEndTimestamp * 1000 + SGT_OFFSET);

					const startHours = sgStart.getUTCHours();
					const startMinutes = sgStart.getUTCMinutes();
					const startSeconds = sgStart.getUTCSeconds();
					const endHours = sgEnd.getUTCHours();
					const endMinutes = sgEnd.getUTCMinutes();
					const endSeconds = sgEnd.getUTCSeconds();
					
					occurrences.forEach(occurrence => {
						// RRule returns UTC dates — extract the calendar date from it directly
						// then reconstruct as a Singapore-midnight-equivalent timestamp
						const year = occurrence.getUTCFullYear();
						const month = occurrence.getUTCMonth();
						const day = occurrence.getUTCDate();

						// Build the occurrence in SGT by treating the RRule date components
						// as SGT calendar values, then subtracting the offset for UTC storage
						const occurrenceStart = new Date(Date.UTC(year, month, day, startHours, startMinutes, startSeconds) - SGT_OFFSET);
						const occurrenceEnd = new Date(Date.UTC(year, month, day, endHours, endMinutes, endSeconds) - SGT_OFFSET);

						const occurrenceTimestamp = Math.floor(occurrenceStart.getTime() / 1000);
						const occurrenceDateStr = this.timestampToSingaporeDate(occurrenceTimestamp);
						
						if (occupiedDates.has(occurrenceDateStr)) return;
						occupiedDates.add(occurrenceDateStr);
						
						expandedEvents.push({
							...masterEvent,
							event_id: `${masterEvent.event_id}_${occurrenceStart.getTime()}`,
							start_time: {
								timestamp: Math.floor(occurrenceStart.getTime() / 1000),
								timezone: masterEvent.start_time.timezone
							},
							end_time: {
								timestamp: Math.floor(occurrenceEnd.getTime() / 1000),
								timezone: masterEvent.end_time.timezone
							},
							is_recurring_instance: true,
							original_event_id: masterEvent.event_id,
							is_all_day: isAllDayEvent
						});
					});
				} catch (e) {
					console.error('Failed to parse recurrence rule:', masterEvent.recurrence, e);
					expandedEvents.push(masterEvent);
				}
			});
			
			singleEvents.forEach(event => {
				if (event.status !== 'cancelled') {
					expandedEvents.push(event);
				}
			});
			
			return expandedEvents;
		},

		formatEvents(events) {
			const formattedEvents = [];
			
			events.forEach(event => {
				if (!event.start_time || !event.start_time.timestamp || !event.summary) return;
				
				const color = this.getEventColor(event.color);
				const startTimestamp = parseInt(event.start_time.timestamp);
				const endTimestamp = event.end_time ? parseInt(event.end_time.timestamp) : null;
				const isAllDay = event.is_all_day !== undefined ? event.is_all_day : this.isAllDayEvent(event);
				
				if (isAllDay) {
					// Apply Singapore offset (+8h) before reading as UTC,
					// same as timed events — timestamps are in SGT not UTC
					const formatAllDayDate = (timestamp) => {
						const date = new Date(timestamp * 1000);
						const sgDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
						const year = sgDate.getUTCFullYear();
						const month = String(sgDate.getUTCMonth() + 1).padStart(2, '0');
						const day = String(sgDate.getUTCDate()).padStart(2, '0');
						return `${year}-${month}-${day}`;
					};

					const startStr = formatAllDayDate(startTimestamp);
					// FullCalendar end date is exclusive, so a "today to tomorrow" event
					// needs end = day after the last day
					const endStr = endTimestamp ? formatAllDayDate(endTimestamp + 1) : null;
					
					formattedEvents.push({
						id: event.event_id,
						title: event.summary,
						start: startStr,
						end: endStr,
						desc: event.description || 'No description',
						color: color,
						allDay: true
					});
				} else {
					// Timed events unchanged
					const formatSingaporeDateTime = (timestamp) => {
						const date = new Date(timestamp * 1000);
						const singaporeDate = new Date(date.getTime() + (8 * 60 * 60 * 1000));
						const year = singaporeDate.getUTCFullYear();
						const month = String(singaporeDate.getUTCMonth() + 1).padStart(2, '0');
						const day = String(singaporeDate.getUTCDate()).padStart(2, '0');
						const hours = String(singaporeDate.getUTCHours()).padStart(2, '0');
						const minutes = String(singaporeDate.getUTCMinutes()).padStart(2, '0');
						const seconds = String(singaporeDate.getUTCSeconds()).padStart(2, '0');
						return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
					};
					
					formattedEvents.push({
						id: event.event_id,
						title: event.summary,
						start: formatSingaporeDateTime(startTimestamp),
						end: endTimestamp ? formatSingaporeDateTime(endTimestamp) : null,
						desc: event.description || 'No description',
						color: color,
						allDay: false
					});
				}
			});
			
			return formattedEvents;
		},

		getEventColor(colorCode) {
			const colorMap = {
				'-1': 'Light Steel Blue',
				'-139': 'yellow',
				'-16181': 'grey',
				'-16776961': 'purple',
				'-8388608': 'orange',
				'-39772': 'pink',
				'-16744448': 'green',
				'-16711681': 'cyan'
			};
			return colorMap[String(colorCode)] || 'Light Steel Blue';
		},
        
        /**
         * Check if event is all-day
         */
        isAllDayEvent(event) {
			if (!event.start_time || !event.end_time) return false;
			
			const startTimestamp = parseInt(event.start_time.timestamp);
			const endTimestamp = parseInt(event.end_time.timestamp);
			
			// Get the duration in hours
			const durationHours = (endTimestamp - startTimestamp) / 3600;
			
			const startDate = new Date(startTimestamp * 1000);
			
			// Check if it's a whole day block
			const isWholeDayBlock = event.description === 'Whole Day Block';
			
			// Check if it starts at midnight (0:00) and lasts ~24 hours
			const isMidnightToMidnight = startDate.getUTCHours() === 0 && 
										startDate.getUTCMinutes() === 0 && 
										(durationHours >= 23.5 && durationHours <= 24.5);
			
			// Check if it spans exactly 24 hours
			const isExactly24Hours = Math.abs(durationHours - 24) < 0.1;
			
			return isWholeDayBlock || isMidnightToMidnight || isExactly24Hours;
		},

		handleSelect: function (info) {
			if (info.event.title && info.event.extendedProps.desc) {
				let modalInfo = '<strong>Title:</strong> ' + info.event.title + '<br>' + '<strong>Description:</strong> ' + info.event.extendedProps.desc;
				this.$refs.messageModal.showModal(modalInfo);
			}
		},
		handleEventDidMount(info) {
			const color = info.event.extendedProps.color;

			if (color) {
				// Apply the color to the event dot
				const eventDot = info.el.querySelector('.fc-event-dot');
				if (eventDot) {
					eventDot.style.backgroundColor = color; // Directly set the background color
				}
			}
		},
	}
};
</script>

<template>
	<Layout>
		<PageHeader :title="title" :items="items" />
		<MessageModal ref="messageModal" />
		<Loading v-if="isLoading" />

		<div class="row">
			<div class="col-12 col-md-3">
				<SelectGroup id="branch" displayName="Select Branch" v-model="data.branch" :options="branchOptions" />
			</div>
		</div>

		<div class="row">
			<div class="col-12">
				<div id="calendar"></div>
			</div>
		</div>
	</Layout>
</template>

<style>
.fc-event,
.fc-event-dot {
	background-color: #C7DAEB;
}

.fc-event .fc-event-title,
.fc-event .fc-event-time {
	color: #1F6CB9;
}

.fc-event:hover {
	background-color: #1F6CB9;
	color: #FFF;
}

.fc-event:hover .fc-event-title,
.fc-event:hover .fc-event-time {
	color: #FFF;
}

.fc-event:hover .fc-daygrid-event-dot {
	border-color: #FFF;
}

.fc-event {
	cursor: pointer !important;
}
</style>